import { type FC, Suspense, type Dispatch, type SetStateAction } from 'react';
import { useFragment, graphql } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';

import { useGateSoldPrice } from '../../../shared/hooks/useGateSoldPrice';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';

// components
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { LocalizedSoldIcon } from 'dibs-elements/exports/LocalizedSoldIcon';
import { LocalizedUnavailableIcon } from 'dibs-elements/exports/LocalizedUnavailableIcon';
import { messageSeller } from '../../pdpMessages';

// styles
import styles from './PdpRespUnavailableHeader.scss';
import classnames from 'classnames';

// types
import { type PdpRespUnavailableHeader_item$key as Item } from './__generated__/PdpRespUnavailableHeader_item.graphql';
import { type PdpRespUnavailableHeader_user$key as User } from './__generated__/PdpRespUnavailableHeader_user.graphql';

const SharedGateViewPrice = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "ViewPriceToast" */ '../../../shared/SharedGateViewPrice/SharedGateViewPrice'
        )
);

const PdpSharedContactSellerButton = dibsLazy(
    'PdpUnavailableSellerAccessible',
    () =>
        import(
            /* webpackChunkName: "PdpUnavailableSellerAccessible" */ '../../shared/PdpSharedContactSellerButton'
        )
);

const userFragment = graphql`
    fragment PdpRespUnavailableHeader_user on User {
        ...PdpSharedContactSellerButton_user
        ...SharedGateViewPrice_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespUnavailableHeader_item on Item {
        ...PdpSharedContactSellerButton_item
        ...SharedGateViewPrice_item
        ...useGateSoldPrice_item
        title
        isSold
        photos {
            smallPath
        }
        seller {
            isAccessible
        }
    }
`;

export const PdpRespUnavailableHeader: FC<{
    item: Item;
    user: User | null | undefined;
    setViewPriceToast: Dispatch<SetStateAction<boolean>>;
    isPdpSpacingOptimizationVariant: boolean;
}> = ({ item: itemRef, user: userRef, setViewPriceToast, isPdpSpacingOptimizationVariant }) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { currency } = useRespUnavailableContext();
    const { isSold, seller } = item;
    const LocalizedIcon = isSold ? LocalizedSoldIcon : LocalizedUnavailableIcon;
    const shouldGateSoldPrice = useGateSoldPrice(item, currency);

    return (
        <div
            className={classnames(styles.container, {
                [styles.isPdpSpacingOptimizationVariant]: isPdpSpacingOptimizationVariant,
            })}
            data-tn="pdp-dlos-heading-info"
        >
            <div className={styles.imageContainer}>
                <img
                    src={item.photos?.[0]?.smallPath || ''}
                    alt={item.title || ''}
                    className={styles.image}
                />
            </div>

            <div className={styles.content}>
                <ClientSuspense fallback={<div className={styles.soldIcon} />}>
                    {shouldGateSoldPrice ? (
                        <SharedGateViewPrice
                            item={item}
                            user={user}
                            currency={currency}
                            setViewPriceToast={setViewPriceToast}
                            launchOrigin="sold gating pdp header"
                        />
                    ) : (
                        <LocalizedIcon className={styles.soldIcon} />
                    )}
                </ClientSuspense>
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.title} dataTn="pdp-dlos-heading-title">
                            {item.title}
                        </Heading>
                    )}
                </HeadingLevel>
            </div>
            {seller?.isAccessible && (
                <Suspense fallback={null}>
                    <div className={styles.buttonContainer}>
                        <PdpSharedContactSellerButton
                            user={user}
                            item={item}
                            size="medium"
                            type="transparent"
                            currency={currency}
                            launchOrigin="contact link in sold pdp - topnav"
                        >
                            {messageSeller}
                        </PdpSharedContactSellerButton>
                    </div>
                </Suspense>
            )}
        </div>
    );
};
